import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, FormInstance, Input, Switch, Tooltip, Upload } from 'antd'
import { DeleteOutlined, HolderOutlined, UploadOutlined } from '@ant-design/icons'
import SelectForm from 'components/SelectForm/SelectForm'
import InputPrefix from 'components/InputPrefix/InputPrefix'
import { ArchivoRecurso, RecursoMetadata, TIPOS_RECURSO } from '../interfaces/ConfiguracionRecursos.interfaces'
import { FIELDS_NAMES_RECURSO } from '../interfaces/FieldNamesRecurso'
import {
  buildBeforeUpload,
  construirUrlRecurso,
  extraerMetadataArchivo,
  formatearPeso,
} from '../helpers/ModalRecursos.helper'
import { validarLongitudMax50 } from 'util/funciones'
import useTiposRecurso from '../hooks/useTipoRecurso'

const SIN_ARCHIVOS: ArchivoRecurso[] = []

export const MAX_LONGITUD_NOMBRE = 50
export const MAX_LONGITUD_DESCRIPCION = 150

const nombreSugerido = (file: File) => file.name.replace(/\.[^.]+$/, '').slice(0, MAX_LONGITUD_NOMBRE)

interface SelectorArchivosProps {
  value?: ArchivoRecurso[]
  onChange?: (archivos: ArchivoRecurso[]) => void
  esVideo: boolean
  readOnly: boolean
  multiple: boolean
  urlExistente?: string
  datosDesde?: number
  reordenable?: boolean
}

const SelectorArchivos = ({
  value,
  onChange,
  esVideo,
  readOnly,
  multiple,
  urlExistente,
  datosDesde = 0,
  reordenable = false,
}: SelectorArchivosProps) => {
  const archivos = value ?? SIN_ARCHIVOS

  const [leyendo, setLeyendo] = useState(false)
  const [previews, setPreviews] = useState<string[]>([])
  const [metadatas, setMetadatas] = useState<RecursoMetadata[]>([])

  const firmaArchivos = archivos
    .map(({ archivo }) => `${archivo.name}-${archivo.size}-${archivo.lastModified}`)
    .join('|')

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const files = useMemo(() => archivos.map(({ archivo }) => archivo), [firmaArchivos])

  useEffect(() => {
    const objectUrls = files.map((archivo) => URL.createObjectURL(archivo))
    setPreviews(objectUrls)
    return () => objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl))
  }, [files])

  useEffect(() => {
    let cancelado = false
    Promise.all(files.map(extraerMetadataArchivo)).then((metas) => {
      if (!cancelado) setMetadatas(metas)
    })
    return () => {
      cancelado = true
    }
  }, [files])

  const archivosRef = useRef<ArchivoRecurso[]>(archivos)
  archivosRef.current = archivos

  const handleBeforeUpload = async (file: File) => {
    const valido = buildBeforeUpload(esVideo ? TIPOS_RECURSO.VIDEO : TIPOS_RECURSO.IMAGEN)(file)
    if (valido !== true) return valido

    setLeyendo(true)
    try {
      const nuevo: ArchivoRecurso = { archivo: file, nombre: nombreSugerido(file), descripcion: '' }
      const siguientes = multiple ? [...archivosRef.current, nuevo] : [nuevo]
      archivosRef.current = siguientes
      onChange?.(siguientes)
    } finally {
      setLeyendo(false)
    }
    return false
  }

  const actualizarLista = (siguientes: ArchivoRecurso[]) => {
    archivosRef.current = siguientes
    onChange?.(siguientes)
  }

  const actualizarArchivo = (indice: number, cambios: Partial<ArchivoRecurso>) =>
    actualizarLista(archivos.map((item, i) => (i === indice ? { ...item, ...cambios } : item)))

  const quitarArchivo = (indice: number) => actualizarLista(archivos.filter((_, i) => i !== indice))

  const [filaArrastrable, setFilaArrastrable] = useState<number | null>(null)
  const [origen, setOrigen] = useState<number | null>(null)
  const [destino, setDestino] = useState<number | null>(null)

  const limpiarArrastre = () => {
    setFilaArrastrable(null)
    setOrigen(null)
    setDestino(null)
  }

  const moverArchivo = (desde: number, hasta: number) => {
    if (desde === hasta) return

    const siguientes = [...archivos]
    const [movido] = siguientes.splice(desde, 1)
    siguientes.splice(hasta, 0, movido)
    actualizarLista(siguientes)
  }

  const propsArrastre = (indice: number) =>
    !reordenable || readOnly
      ? {}
      : {
          draggable: filaArrastrable === indice,
          onDragStart: () => setOrigen(indice),
          onDragOver: (e: React.DragEvent) => {
            e.preventDefault()
            if (origen !== null && destino !== indice) setDestino(indice)
          },
          onDrop: (e: React.DragEvent) => {
            e.preventDefault()
            if (origen !== null) moverArchivo(origen, indice)
            limpiarArrastre()
          },
          onDragEnd: limpiarArrastre,
        }

  const estiloFila = (indice: number): React.CSSProperties => ({
    ...contenedorPreview,
    ...(origen === indice ? { opacity: 0.4 } : {}),
    ...(destino === indice && origen !== indice ? { borderColor: '#d48806', background: '#fffbe6' } : {}),
  })

  const textoBoton = archivos.length > 0 || urlExistente ? 'Agregar otro archivo' : 'Seleccionar archivo'

  return (
    <>
      <Upload
        accept={esVideo ? '.mp4' : '.png,.webp'}
        beforeUpload={handleBeforeUpload}
        multiple={multiple}
        showUploadList={false}
        fileList={[]}
        disabled={readOnly || leyendo}
      >
        <Button icon={<UploadOutlined />} loading={leyendo} disabled={readOnly}>
          {multiple ? textoBoton : archivos.length > 0 || urlExistente ? 'Cambiar archivo' : 'Seleccionar archivo'}
        </Button>
      </Upload>

      {archivos.length === 0 && urlExistente && (
        <div style={contenedorPreview}>
          {esVideo ? (
            <video src={urlExistente} controls style={estiloVideo} />
          ) : (
            <img src={urlExistente} alt="Previsualización del recurso" style={estiloImagen} />
          )}
          <div style={{ fontSize: 12, color: '#595959' }}>Archivo actual del recurso</div>
        </div>
      )}

      {archivos.length > 0 && (
        <div style={listaPreviews}>
          {archivos.map(({ archivo, nombre, descripcion }, indice) => (
            <div
              key={`${archivo.name}-${archivo.lastModified}-${indice}`}
              style={estiloFila(indice)}
              {...propsArrastre(indice)}
            >
              {reordenable && (
                <Tooltip title="Arrastra para cambiar el orden en que se muestra">
                  <div
                    style={columnaOrden}
                    onMouseDown={() => !readOnly && setFilaArrastrable(indice)}
                    onMouseUp={() => setFilaArrastrable(null)}
                  >
                    <span style={numeroOrden}>{indice + 1}</span>
                    {!readOnly && <HolderOutlined style={{ color: '#8c8c8c', cursor: 'grab' }} />}
                  </div>
                </Tooltip>
              )}

              {previews[indice] &&
                (esVideo ? (
                  <video src={previews[indice]} controls style={estiloVideo} />
                ) : (
                  <img src={previews[indice]} alt={archivo.name} style={estiloImagen} draggable={false} />
                ))}

              <div style={{ flex: 1, minWidth: 0 }}>
                {indice >= datosDesde ? (
                  <>
                    <Input
                      size="small"
                      value={nombre}
                      maxLength={MAX_LONGITUD_NOMBRE}
                      disabled={readOnly}
                      placeholder="Nombre del recurso"
                      onChange={(e) => actualizarArchivo(indice, { nombre: e.target.value })}
                    />
                    <Input
                      size="small"
                      value={descripcion}
                      maxLength={MAX_LONGITUD_DESCRIPCION}
                      disabled={readOnly}
                      placeholder="Descripción (opcional): texto sobre el recurso"
                      style={{ marginTop: 6 }}
                      onChange={(e) => actualizarArchivo(indice, { descripcion: e.target.value })}
                    />
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: '#595959' }}>
                    <div style={{ fontWeight: 600, wordBreak: 'break-all' }}>{archivo.name}</div>
                    <div style={{ color: '#8c8c8c' }}>Reemplaza el archivo actual del recurso</div>
                  </div>
                )}

                {metadatas[indice] && (
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                    {metadatas[indice].ancho && metadatas[indice].alto
                      ? `${metadatas[indice].ancho} × ${metadatas[indice].alto} px · `
                      : ''}
                    {formatearPeso(metadatas[indice].peso_bytes)}
                    {metadatas[indice].duracion_segundos ? ` · ${metadatas[indice].duracion_segundos} s` : ''}
                  </div>
                )}
              </div>

              {!readOnly && (
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => quitarArchivo(indice)} />
              )}
            </div>
          ))}
        </div>
      )}

      {reordenable && archivos.length > 1 && (
        <span style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4, display: 'block' }}>
          El número es el orden entre estos archivos: arrastra las filas para cambiarlo. Se agregan
          después de los recursos que la estación ya tenga.
        </span>
      )}

      {archivos.length > 2 && (
        <span style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4, display: 'block' }}>
          {archivos.length} archivos adjuntos · desplázate en la lista para verlos todos.
        </span>
      )}

      <span style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4, display: 'block' }}>
        Solo se permiten archivos {esVideo ? '.mp4' : '.png o .webp'}
        {multiple ? '. Puedes adjuntar varios: cada uno se guarda como un recurso aparte.' : '.'}
      </span>
    </>
  )
}

const ALTO_FILA_PREVIEW = 106

const listaPreviews: React.CSSProperties = {
  maxHeight: ALTO_FILA_PREVIEW * 2,
  overflowY: 'auto',
  paddingRight: 4,
}

const contenedorPreview: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  alignItems: 'center',
  marginTop: 8,
  padding: 8,
  border: '1px solid #f0f0f0',
  borderRadius: 8,
}

const columnaOrden: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  flexShrink: 0,
  cursor: 'grab',
  userSelect: 'none',
}

const numeroOrden: React.CSSProperties = {
  width: 22,
  height: 22,
  lineHeight: '22px',
  textAlign: 'center',
  borderRadius: '50%',
  background: '#fff7e6',
  border: '1px solid #ffd591',
  color: '#d48806',
  fontSize: 12,
  fontWeight: 700,
}

const estiloVideo: React.CSSProperties = {
  width: 140,
  height: 80,
  objectFit: 'cover',
  borderRadius: 6,
  background: '#000',
  flexShrink: 0,
}

const estiloImagen: React.CSSProperties = {
  width: 140,
  height: 80,
  objectFit: 'contain',
  borderRadius: 6,
  background: '#fafafa',
  flexShrink: 0,
}

interface PasoDatosRecursoProps {
  form: FormInstance
  readOnly: boolean
  mostrarEstado?: boolean
  permitirVariosArchivos?: boolean
  esEdicion?: boolean
}

const PasoDatosRecurso = ({
  form,
  readOnly,
  mostrarEstado = true,
  permitirVariosArchivos = false,
  esEdicion = false,
}: PasoDatosRecursoProps) => {
  const { options: tiposOptions, loading: loadingTipos } = useTiposRecurso()

  const tipoId: number = Form.useWatch(FIELDS_NAMES_RECURSO.TIPO, form) ?? TIPOS_RECURSO.IMAGEN
  const url: string | undefined = Form.useWatch(FIELDS_NAMES_RECURSO.URL, { form, preserve: true })
  const activo: boolean = Form.useWatch(FIELDS_NAMES_RECURSO.ACTIVO, { form, preserve: true }) ?? true
  const archivos: ArchivoRecurso[] =
    Form.useWatch(FIELDS_NAMES_RECURSO.ARCHIVOS, { form, preserve: true }) ?? []

  const esRecursoExistente = esEdicion || readOnly
  const datosDesde = esRecursoExistente ? 1 : 0

  const tipoPrevio = useRef(tipoId)
  useEffect(() => {
    if (tipoPrevio.current !== tipoId) {
      tipoPrevio.current = tipoId
      form.setFieldValue(FIELDS_NAMES_RECURSO.ARCHIVOS, [])
    }
  }, [tipoId])

  return (
    <>
      <Form.Item
        label="TIPO"
        name={FIELDS_NAMES_RECURSO.TIPO}
        rules={[{ required: true, message: 'Seleccione el tipo de recurso' }]}
        initialValue={TIPOS_RECURSO.IMAGEN}
      >
        <SelectForm options={tiposOptions} loading={loadingTipos} disabled={readOnly || esEdicion} />
      </Form.Item>

      {esRecursoExistente && (
        <>
          <InputPrefix
            name={FIELDS_NAMES_RECURSO.NOMBRE}
            rules={[
              { required: true, whitespace: true, message: 'Ingrese el nombre del recurso' },
              { validator: validarLongitudMax50 },
            ]}
            prefix="Nombre*"
            placeholder="Ej. Banner promocional"
            disabled={readOnly}
          />

          <InputPrefix
            name={FIELDS_NAMES_RECURSO.DESCRIPCION}
            prefix="Descripción"
            placeholder="Texto que se muestra sobre el recurso (opcional)"
            maxLength={MAX_LONGITUD_DESCRIPCION}
            disabled={readOnly}
          />

          <InputPrefix
            name={FIELDS_NAMES_RECURSO.ORDEN}
            prefix="Orden"
            type="number"
            min={1}
            placeholder="Posición con la que se muestra en la estación"
            disabled={readOnly}
          />
        </>
      )}

      {esEdicion && (
        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: -8, marginBottom: 12 }}>
          {archivos.length > 1
            ? `El primer archivo reemplaza el actual (el anterior se elimina) y los otros ${archivos.length - 1} se agregan como recursos nuevos en la misma estación, con el nombre y la descripción de su fila.`
            : 'Si adjuntas un archivo reemplaza el actual y el anterior se elimina. Puedes adjuntar más para agregarlos como recursos nuevos en la misma estación.'}
        </div>
      )}

      <Form.Item
        label={permitirVariosArchivos ? 'ARCHIVOS' : 'ARCHIVO'}
        name={FIELDS_NAMES_RECURSO.ARCHIVOS}
        rules={[
          {
            validator: (_, seleccionados: ArchivoRecurso[] = []) => {
              if (seleccionados.length === 0 && !url) {
                return Promise.reject(new Error('Cargue al menos un archivo'))
              }

              const sinNombre = seleccionados
                .slice(datosDesde)
                .some(({ nombre }) => !nombre || !nombre.trim())

              return sinNombre
                ? Promise.reject(new Error('Escriba el nombre de cada archivo adjunto'))
                : Promise.resolve()
            },
          },
        ]}
      >
        <SelectorArchivos
          esVideo={tipoId === TIPOS_RECURSO.VIDEO}
          readOnly={readOnly}
          multiple={permitirVariosArchivos}
          urlExistente={url}
          datosDesde={datosDesde}
          reordenable={!esRecursoExistente}
        />
      </Form.Item>

      {mostrarEstado && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 600 }}>Estado</div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Habilita este recurso para su uso.</div>
          </div>
          <Switch
            checked={activo}
            disabled={readOnly}
            onChange={(checked) => form.setFieldValue(FIELDS_NAMES_RECURSO.ACTIVO, checked)}
          />
        </div>
      )}
    </>
  )
}

export default PasoDatosRecurso
